import { Component, Output, EventEmitter, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { ActionsService } from 'src/app/shared/services/actions.service';
import { EscalonVista } from '../../../home-rates/models/home-rates.model';
import { CotizacionDesdeUsdt } from '../../clp-usdt.tramos';

@Component({
  selector: 'app-calculator-crypto',
  templateUrl: './calculator.component.html',
  styleUrls: ['./calculator.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CalculatorCryptoComponent implements OnChanges{

  public minError = false;
  public clpCurrency: number = 0
  public usdtCurrency: number = 0;
  public usdtDisplay = '';
  private minValue: number = 20;
  private updatingFromClp = false;
  private updatingFromUsdt = false;
  private isUsdtFocused = false;
  /** Los USDT tal como los escribió el cliente (la cotización por USDT parte siempre de acá). */
  private usdtEscrito = 0;
  @Input()
  public usdt_clp_tax: number = 0;

  /** Tramos CLP→USDT: mejor tasa publicada («Desde X»). Null = no se muestra. */
  @Input()
  public desdeTasa: number | null = null;

  /** Tramos CLP→USDT: CLP que ahorra con el monto actual frente a la base. 0 = no se muestra. */
  @Input()
  public ahorroClp: number = 0;

  /** Tramos CLP→USDT para `app-escalera-tramos`. Null = no se dibuja (como antes). */
  @Input()
  public escaleraClp: EscalonVista[] | null = null;

  /** Base de los tramos: el tramo se elige con `montoClp ÷ base`, como el back. */
  @Input()
  public baseTramos: number | null = null;

  /** `precioCorte` de los tramos: si viene, el tramo se elige con `montoClp ÷ precioCorte`. Null = `÷ baseTramos`. */
  @Input()
  public precioCorteTramos: number | null = null;

  /**
   * Con tramos: cotiza el monto escrito en USDT (tasa por punto fijo o, si no
   * hay, el primer peso del tramo; ver `cotizarDesdeUsdt`). Null o que
   * devuelva null = `clp = usdt × tasa`, como siempre.
   */
  @Input()
  public cotizarUsdt: ((usdt: number) => CotizacionDesdeUsdt | null) | null = null;

  @Input()
  public saldoActual: number = 0;

  @Input()
  public sobregiroDisponible: number | undefined = 0;

  @Input()
  public saldoContable: number | undefined = 0;

  @Input()
  public lineaCreditoBloqueada: boolean | undefined = false;

  @Input()
  public lineaCredito: number | undefined = 0;

  @Output()
  public changeStep = new EventEmitter<number>();

  @Output()
  public clpCurrencyEmit = new EventEmitter<number>();

  @Output()
  public usdCurrencyEmit = new EventEmitter<number>();

  @Output()
  public onBuyClick = new EventEmitter<{ clpAmount: number; usdtAmount: number; rate: number }>();

  constructor(
    private actionService: ActionsService,
  ) {
    this.actionService.subjectTasaUSDT.subscribe(
      (res) => {
        // setTimeout(()=> this.transformCurrency('clp'), 1000)
      }
    )
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['usdt_clp_tax']) {

        if (this.updatingFromClp || this.updatingFromUsdt) {
          this.recalculateCurrencies();
        }
      
    }
  }

  get valorClp(): string {
    return this.clpCurrency === 0 ? '' : this.clpCurrency.toLocaleString('en-US');
  }

  set valorClp(val: string) {
    if (val === '') {
      this.clpCurrency = 0;
    } else {
      this.clpCurrency = parseFloat(val.replace(/,/g, ''));
    }
  }

  onUsdtInput(value: string): void {
    this.usdtDisplay = value;
    this.usdtCurrency = parseFloat(value) || 0;
    this.usdtEscrito = this.usdtCurrency;
    this.transformCurrency('usdt');
  }

  /**
   * Con tramos, los pesos (y los USDT que de verdad se acreditan) del monto
   * escrito en USDT. True si se aplicó; false = la cuenta de siempre.
   */
  private aplicarCotizacionUsdt(): boolean {
    const q = this.cotizarUsdt ? this.cotizarUsdt(this.usdtEscrito) : null;
    if (!q || !(q.clp > 0)) return false;
    this.clpCurrency = q.clp;
    // Si hubo que subir al tramo, se acredita un poco más: se muestra eso.
    this.usdtCurrency = q.usdt;
    return true;
  }

  onUsdtFocus(): void {
    this.isUsdtFocused = true;
    if (this.usdtCurrency > 0) {
      this.usdtDisplay = String(this.usdtCurrency);
    }
  }

  onUsdtBlur(): void {
    this.isUsdtFocused = false;
    this.formatUsdtDisplay();
  }

  private formatUsdtDisplay(): void {
    this.usdtDisplay = this.usdtCurrency === 0 ? '' : this.usdtCurrency.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  emitChangeStep(): void {
    if (this.minError) return;
    this.clpCurrencyEmit.emit(+this.clpCurrency);
    this.usdCurrencyEmit.emit(+this.usdtCurrency);
    // Emitir evento para abrir modal de selección de destino
    this.onBuyClick.emit({
      clpAmount: +this.clpCurrency,
      usdtAmount: +this.usdtCurrency,
      rate: this.usdt_clp_tax
    });
  }

  // Método para continuar al siguiente paso (llamado desde el padre después del modal)
  proceedToNextStep(): void {
    this.changeStep.emit(2);
  }

  emitAmountChange(): void {
    this.clpCurrencyEmit.emit(+this.clpCurrency);
    this.transformCurrency('clp');
  }

  maxValAvailableCLP(): void {
    if (this.lineaCredito != null && this.lineaCredito > 0 && !this.lineaCreditoBloqueada) {
      if (this.saldoContable != null && this.saldoContable > 0) {
        this.valorClp = this.saldoContable?.toString() ?? '';
        this.transformCurrency('clp');
      }
    } else {
      if (this.saldoActual > 0) {
        this.valorClp = this.saldoActual.toString();
        this.transformCurrency('clp');
      }
    }
  }

  /** Tope para «Usar X» de la escalera: el mismo saldo que usa MÁX. */
  get maximoClp(): number {
    if (this.lineaCredito != null && this.lineaCredito > 0 && !this.lineaCreditoBloqueada) {
      return this.saldoContable ?? 0;
    }
    return this.saldoActual;
  }

  /** «Usar X» de la escalera: escribe los pesos en el campo, por el mismo camino que MÁX. */
  usarMontoClp(monto: number): void {
    if (!(monto > 0) || this.lineaCreditoBloqueada) return;
    this.valorClp = String(Math.ceil(monto));
    this.transformCurrency('clp');
  }

  clearValAvailableCLP(): void {
    this.valorClp = "0";
    this.transformCurrency('clp');
  }

  transformCurrency(currency: string): void {
    this.minError = false;
    let actualRate = this.usdt_clp_tax;

    if (currency === 'clp' && !this.updatingFromUsdt) {
        this.updatingFromClp = true;
        this.usdtCurrency = this.clpCurrency / this.usdt_clp_tax;
        if (!this.isUsdtFocused) {
          this.formatUsdtDisplay();
        }
    }

    if (currency === 'usdt' && !this.updatingFromClp) {
        this.updatingFromUsdt = true;
        if (!this.aplicarCotizacionUsdt()) {
          this.clpCurrency = this.usdtCurrency * this.usdt_clp_tax;
        }
    }
    this.usdCurrencyEmit.emit(this.usdtCurrency);
    this.clpCurrencyEmit.emit(+this.clpCurrency);


    if (this.usdtCurrency < this.minValue) {
        this.minError = true;
    }

    setTimeout(() => {
        if (actualRate !== this.usdt_clp_tax) {
        } else {
            this.updatingFromClp = false;
            this.updatingFromUsdt = false;
        }
    }, 200);

}

  recalculateCurrencies(): void {
      if (this.updatingFromClp) {
        this.usdtCurrency = this.clpCurrency / this.usdt_clp_tax;
        this.formatUsdtDisplay();
      }
    
      if (this.updatingFromUsdt) {
        if (this.aplicarCotizacionUsdt()) {
          // Con tramos se emite siempre el CLP recalculado: es el que elige el
          // tramo. Fuera del ngOnChanges (microtarea) para no cambiarle al
          // padre lo que ya se dibujó en esta pasada.
          this.valorClp = this.clpCurrency.toLocaleString('en-US');
          if (!this.isUsdtFocused) this.formatUsdtDisplay();
          const clp = +this.clpCurrency;
          const usdt = this.usdtCurrency;
          Promise.resolve().then(() => {
            this.usdCurrencyEmit.emit(usdt);
            this.clpCurrencyEmit.emit(clp);
          });
          return;
        }
        this.clpCurrency = this.usdtCurrency * this.usdt_clp_tax;
        this.valorClp = this.clpCurrency.toLocaleString('en-US');
      }
    
  }

  soloNumeros(event: KeyboardEvent) {
    const char = event.key;
    if (!char.match(/[0-9]/)) {
      event.preventDefault();
    }
  }

  soloNumerosDecimal(event: KeyboardEvent) {
    const char = event.key;
    if (!char.match(/[0-9.]/)) {
      event.preventDefault();
      return;
    }
    const input = event.target as HTMLInputElement;
    if (char === '.' && input.value.includes('.')) {
      event.preventDefault();
    }
  }
}
